import {volumetricKg} from './volumetric'
import {hasEvidence} from './evidenceScore'

/**
 * Pure logic behind EvidenceIntake.js — the single-record evidence-capture
 * flow added to DisputeDrawer.js in Phase C (L×B×H and dead-weight inputs,
 * the photo/video gate and product details). Same split as
 * Freeze's state/dwsUpload.js: this file holds the rules, the component
 * holds the form state, and the tests below exercise the rules without
 * mounting anything.
 *
 * Session-only, exactly like EvidenceStudio.js's applyEvidenceToRow: nothing
 * here calls an API, and nothing it returns is persisted anywhere beyond the
 * caller's own React state (see index.js).
 */

export const REQUIREMENT = {
  DIMENSIONS: 'DIMENSIONS',
  DEAD_WEIGHT: 'DEAD_WEIGHT',
  EVIDENCE: 'EVIDENCE',
}

/** Keyed labels, not an assembled sentence — see uploadMissing's own note in
 * freeze-v2/state/dwsUpload.js for why: a joined string can't be reused as
 * both tooltip and aria-label under future copy edits or i18n. */
export const MISSING_LABEL = {
  // One entry for all three dimensions rather than three entries: a merchant
  // with two of them filled is told the same thing either way, and "length,
  // breadth" reads as two separate problems when it is one incomplete box.
  [REQUIREMENT.DIMENSIONS]: 'the length, breadth and height of the parcel',
  [REQUIREMENT.DEAD_WEIGHT]: 'the dead weight of the parcel',
  [REQUIREMENT.EVIDENCE]: 'a photo or a video of the shipment',
}

/**
 * Everything a dispute needs before it can be submitted, as a LIST of what is
 * still missing — so the disabled Submit button and the tooltip explaining why
 * read off one function rather than duplicating the rule in two places.
 *
 * Three requirements, and the third is deliberately an OR:
 *   - all three dimensions, because a partial box computes no volumetric;
 *   - the dead weight;
 *   - a photo OR a video, never both. One packing video settles a dispute on
 *     its own, so demanding stills alongside it would be asking for work that
 *     changes no outcome.
 *
 * Returned in form order, not severity order: the tooltip reads as a walk down
 * the screen, so a merchant fixes them top to bottom instead of hunting.
 *
 * Dimensions and dead weight became mandatory on request. They were optional
 * when this flow could fall back to "keep what is already on file" — but a
 * dispute argues that the courier's own figures are wrong, and one that states
 * no competing figure is not an argument.
 */
export function missingForSubmit({dimsTyped, weightTyped, hasImages, hasVideo} = {}) {
  const out = []
  if (!dimsTyped) out.push(REQUIREMENT.DIMENSIONS)
  if (!weightTyped) out.push(REQUIREMENT.DEAD_WEIGHT)
  // Asked of evidenceScore.js's hasEvidence(), the module's single answer to
  // "is there proof", rather than re-spelling `!video && !images` here. The
  // form counts filled slots and holds a flag, so the two facts are handed
  // over in the row shape that predicate reads.
  if (!hasEvidence({merchantImages: hasImages ? 1 : 0, hasVideo})) out.push(REQUIREMENT.EVIDENCE)
  return out
}

/**
 * The same three requirements, asked of a ROW instead of the capture form —
 * this is the gate on RAISING a dispute (actions.js), where the form's gate is
 * on submitting one evidence payload.
 *
 * Both read missingForSubmit, deliberately: the drawer shows the form and the
 * Dispute button one above the other, and two functions answering "is this
 * shipment ready" would eventually answer differently in the same drawer.
 *
 * The two figure requirements are read off the MERCHANT's side of the row —
 * `declaredDims` is the box the merchant declared and `originalWeight` the
 * weight they declared with it, the figure `excessKg` is computed against.
 * `claimedDeadKg` is honoured first because it is the scale reading the
 * merchant typed into the evidence form, i.e. their most recent statement of
 * the same fact. The courier's own `dims`/`deadKg` are NOT accepted here: they
 * are the bill being argued against, so satisfying the gate with them would
 * let a dispute state no competing figure at all.
 */
export function missingForDispute(row) {
  const dims = (row && row.declaredDims) || {}
  const dead = Number((row && row.claimedDeadKg) || (row && row.originalWeight)) || 0
  return missingForSubmit({
    dimsTyped: Number(dims.l) > 0 && Number(dims.b) > 0 && Number(dims.h) > 0,
    weightTyped: dead > 0,
    hasImages: (Number(row && row.merchantImages) || 0) > 0,
    hasVideo: !!(row && row.hasVideo),
  })
}

/**
 * The one sentence that explains a blocked submit or a blocked dispute, built
 * from MISSING_LABEL so a copy edit lands on every surface at once — the
 * form's tooltip, its aria-label, the drawer footer's disabled Dispute button
 * and the error toast applyTransition raises all print this.
 */
export function missingSentence(missing) {
  const list = missing || []
  if (!list.length) return ''
  return `Still needed: ${list.map((k) => MISSING_LABEL[k]).join(', ')}.`
}

/**
 * Product URL is optional (Phase C5) — an empty value is always fine. Only a
 * non-empty, malformed value is flagged, checked the same way the mockup
 * does: `new URL()` in a try/catch.
 */
export function isValidProductUrl(value) {
  const v = String(value || '').trim()
  if (!v) return true
  try {
    // eslint-disable-next-line no-new
    new URL(v)
    return true
  } catch {
    return false
  }
}

/**
 * Applies one evidence-intake submission to a single row. Returns a
 * brand-new row (never mutates `row`) — video/images are additive, the same
 * "never destructive" contract as EvidenceStudio.js's applyEvidenceToRow.
 *
 * EVERYTHING THE MERCHANT TYPES LANDS ON THE MERCHANT'S SIDE OF THE ROW.
 * A row carries two sides: what the courier measured and billed (`dims`,
 * `deadKg`, `volKg`, `appliedWeight`) and what the merchant says the parcel
 * is (`declaredDims`, `declaredVolKg`, `originalWeight`) — RecordSections.js
 * renders them as the "Declared" and "Billed" cards. The billed side is a
 * fact of the bill under dispute; a merchant filling in this form is making
 * a CLAIM against it, and a claim may not restate the bill.
 *
 * That is not only a semantic point. data/noberoDisputes.test.js asserts
 * `max(deadKg, volKg) === appliedWeight` (and `min(...) < appliedWeight`),
 * and state/query.js's governingType() reads exactly those two figures to
 * decide which the grid and drawer bold. Writing merchant figures into
 * deadKg/volKg breaks that pair apart, so the UI would then bold a governing
 * weight the numbers no longer support. This function used to do precisely
 * that with typed dimensions — dims + a recomputed volKg — which is why the
 * dimensions now go to declaredDims/declaredVolKg instead.
 *
 * Dead weight gets a field of its own, `claimedDeadKg`, because the merchant
 * side has no free slot for it: `originalWeight` is the declared figure that
 * `excessKg`/`excessAmount` and every recovery total are computed from
 * (`excessKg === appliedWeight - originalWeight`, also asserted), so letting
 * a form field overwrite it would let a merchant restate the amount they are
 * claiming back. `claimedDeadKg` is additive and read by nothing that
 * computes money or governance, so no invariant can be violated by it.
 *
 * Absent dimensions or an absent weight leave the corresponding fields
 * completely untouched — this never invents a box or a scale reading,
 * mirroring buildUploadRecord's own refusal
 * (freeze-v2/state/dwsUpload.js:98-124).
 */
export function applyEvidenceIntake(row, payload) {
  if (!row) throw new Error('applyEvidenceIntake requires a row')
  const addImages = (payload && payload.addImages) || 0
  const addVideo = !!(payload && payload.addVideo)
  const dim = payload && payload.dim
  const l = Number(dim && dim.l)
  const b = Number(dim && dim.b)
  const h = Number(dim && dim.h)
  const typed = l > 0 && b > 0 && h > 0
  const weight = Number(payload && payload.weightKg)
  const weighed = weight > 0

  return {
    ...row,
    merchantImages: (row.merchantImages || 0) + addImages,
    hasVideo: row.hasVideo || addVideo,
    // volumetricKg via the shared helper, never inline, so the printed carton
    // and the volumetric beside it can never disagree.
    ...(typed ? {declaredDims: {l, b, h}, declaredVolKg: volumetricKg(l, b, h)} : {}),
    ...(weighed ? {claimedDeadKg: weight} : {}),
    productCategory: payload && 'productCategory' in payload ? payload.productCategory : row.productCategory,
    productUrl: payload && 'productUrl' in payload ? payload.productUrl : row.productUrl,
  }
}
